import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { SelectedLocationService } from '../../../service/selected-location.service';
import { ScreenSizeService } from '../../../service/screen-size.service';
import { SofReceivingStateService } from '../../../domain/sof-receiving/sof-receiving-state.service';
import { SofReceivingOrder } from '../../../models/sof-receiving/SofReceivingOrder.interface';

/**
 * @component SofReceivingPageComponent
 *
 * Page shell for the SOF Receiving feature. It is the route target for both
 * `/sof-receiving` (active queue, editable) and `/sof-receiving/history`
 * (read-only) — the history route sets `readOnly: true` via route data.
 *
 * Responsibilities:
 * - Subscribes to the host app's SelectedLocationService (the nav's
 *   select-location component writes to it). There is NO SOF-specific facility
 *   picker: the queue always reflects the host location, mapped through the
 *   explicit storage-facility map. A nav location change reloads the queue and
 *   clears any open SOF (its data belongs to the old location).
 * - Subscribes to ScreenSizeService for the responsive split:
 *   mobile (small screen) = queue page → detail page card flow;
 *   desktop = master-detail on one screen (queue rail + detail pane).
 * - Hosts the To Pick/History segment navigation.
 */
@Component({
  selector: 'app-sof-receiving-page',
  templateUrl: './sof-receiving-page.component.html',
  styleUrls: ['./sof-receiving-page.component.css'],
})
export class SofReceivingPageComponent implements OnInit, OnDestroy {
  public readOnly = false;
  public isSmallScreen = false;

  private _locationSubscription!: Subscription;
  private _screenSizeSubscription!: Subscription;
  private _selectedOrderSubscription!: Subscription;

  public hasOpenSof = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _selectedLocationService: SelectedLocationService,
    private _screenSizeService: ScreenSizeService,
    public state: SofReceivingStateService
  ) {}

  ngOnInit(): void {
    this.readOnly = this._route.snapshot.data['readOnly'] === true;
    this.state.readOnly = this.readOnly;
    this.state.clearSelection();

    this._screenSizeSubscription =
      this._screenSizeService.isSmallScreen.subscribe((isSmallScreen) => {
        this.isSmallScreen = isSmallScreen;
      });

    // Fires immediately with the current location (BehaviorSubject), which
    // triggers the initial queue load; later emissions are nav location
    // changes and reload the queue while clearing any open SOF.
    this._locationSubscription =
      this._selectedLocationService.selectedLocation$.subscribe((location) => {
        this.state.setLocation(location);
      });

    this._selectedOrderSubscription = this.state.selectedOrder.subscribe(
      (order) => {
        this.hasOpenSof = order !== null;
      }
    );
  }

  ngOnDestroy(): void {
    this._locationSubscription.unsubscribe();
    this._screenSizeSubscription.unsubscribe();
    this._selectedOrderSubscription.unsubscribe();
  }

  /** To Pick/History segment — navigates between the two SOF routes. */
  public onSegmentChange(event: any): void {
    const value = event?.detail?.value;
    if (value === 'history' && !this.readOnly) {
      this._router.navigate(['/sof-receiving/history']);
    } else if (value === 'active' && this.readOnly) {
      this._router.navigate(['/sof-receiving']);
    }
  }

  public onSofSelected(order: SofReceivingOrder): void {
    this.state.selectSof(order);
  }

  /** Mobile back button on the detail view — returns to the queue. */
  public onBackToQueue(): void {
    this.state.clearSelection();
  }
}
